import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import OrgTree from "./OrgTree";
import "./HierarchyChart.css";

const ZOOMS = [0.5, 0.65, 0.8, 1, 1.15, 1.3];
const EMPTY = [];

/** Every node with its ancestors' ids (for search / expand-all). */
const indexTree = (nodes, ancestors = [], out = new Map()) => {
  nodes.forEach((n) => {
    out.set(n.id, { node: n, ancestors });
    indexTree(n.children || EMPTY, [...ancestors, n.id], out);
  });
  return out;
};

/** True below 768px (the chart falls back to the indented list). */
const useNarrow = () => {
  const query = "(max-width: 767.98px)";
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const on = (e) => setNarrow(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
};

const ChartNode = ({
  node,
  depth,
  open,
  toggle,
  renderCard,
  isInactive,
  matches,
  onOpen,
}) => {
  const kids = node.children || EMPTY;
  const isOpen = open.has(node.id);
  const hit = matches.has(node.id);
  return (
    <li
      role="treeitem"
      aria-expanded={kids.length ? isOpen : undefined}
      aria-selected={hit || undefined}
    >
      <div
        className={`hc-card ${isInactive?.(node) ? "is-inactive" : ""} ${hit ? "is-match" : ""} ${onOpen ? "is-clickable" : ""}`}
        data-node-id={node.id}
        onClick={onOpen ? () => onOpen(node) : undefined}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" && onOpen) onOpen(node);
          if (
            kids.length &&
            (e.key === "ArrowDown" || e.key === "ArrowRight") &&
            !isOpen
          )
            toggle(node.id);
          if (
            kids.length &&
            (e.key === "ArrowUp" || e.key === "ArrowLeft") &&
            isOpen
          )
            toggle(node.id);
        }}
        tabIndex={0}
      >
        {renderCard(node, depth)}
        {kids.length > 0 && (
          <button
            type="button"
            className="hc-toggle"
            onClick={(e) => {
              e.stopPropagation();
              toggle(node.id);
            }}
            aria-label={isOpen ? "Collapse" : `Expand (${kids.length})`}
            aria-expanded={isOpen}
          >
            {isOpen ? (
              <i className="bi bi-dash"></i>
            ) : (
              <span>+{kids.length}</span>
            )}
          </button>
        )}
      </div>
      {kids.length > 0 && isOpen && (
        <ul role="group">
          {kids.map((k) => (
            <ChartNode
              key={k.id}
              node={k}
              depth={depth + 1}
              open={open}
              toggle={toggle}
              renderCard={renderCard}
              isInactive={isInactive}
              matches={matches}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

/**
 * Org-chart view of a tree of { id, children } nodes: top-down cards joined by
 * connector lines (plain CSS), expand/collapse per card (first `openDepth`
 * levels open), zoom, fit, drag to pan, and search (highlights matches and
 * opens the path to them). Below 768px, or with the "List" toggle, it shows
 * the indented OrgTree instead.
 * - renderCard(node, depth): card content (stop propagation on inner buttons)
 * - renderRow(node, depth): row content for the list view (defaults to renderCard)
 * - searchText(node): text the search matches against
 * - onOpen(node): click / Enter on a card
 * - renderTable(query): optional flat table view (adds a "Table" toggle next to
 *   Chart / List); gets the search text
 * - view: "chart" | "list" controlled by the page (hides the view toggle)
 */
const HierarchyChart = ({
  nodes = EMPTY,
  renderCard,
  renderRow,
  searchText,
  isInactive,
  onOpen,
  openDepth = 2,
  label = "Hierarchy",
  renderTable,
  view: controlledView,
}) => {
  const narrow = useNarrow();
  const [ownView, setView] = useState("chart");
  const view = controlledView || ownView;
  const showTable = !!renderTable && view === "table";
  const index = useMemo(() => indexTree(nodes), [nodes]);
  const [open, setOpen] = useState(() => new Set());
  const [zoom, setZoom] = useState(1);
  const [search, setSearch] = useState("");
  const canvasRef = useRef(null);
  const contentRef = useRef(null);
  const drag = useRef(null);

  // Default: first `openDepth` levels open (again when the data changes shape)
  useEffect(() => {
    const ids = new Set();
    index.forEach(({ ancestors }, id) => {
      if (
        ancestors.length < openDepth &&
        (index.get(id).node.children || EMPTY).length
      )
        ids.add(id);
    });
    setOpen(ids);
  }, [index, openDepth]);

  const q = search.trim().toLowerCase();
  const matches = useMemo(() => {
    const set = new Set();
    if (!q || !searchText) return set;
    index.forEach(({ node }, id) => {
      if (
        String(searchText(node) || "")
          .toLowerCase()
          .includes(q)
      )
        set.add(id);
    });
    return set;
  }, [index, q, searchText]);

  // Open the path to every match and bring the first one into view
  useEffect(() => {
    if (!matches.size) return;
    setOpen((prev) => {
      const next = new Set(prev);
      matches.forEach((id) =>
        index.get(id).ancestors.forEach((a) => next.add(a)),
      );
      return next;
    });
    const first = [...matches][0];
    requestAnimationFrame(() => {
      const el = canvasRef.current?.querySelector(
        `[data-node-id="${CSS.escape(String(first))}"]`,
      );
      el?.scrollIntoView({
        block: "nearest",
        inline: "center",
        behavior: "smooth",
      });
    });
  }, [matches, index]);

  const toggle = useCallback(
    (id) =>
      setOpen((s) => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );
  const expandAll = () =>
    setOpen(
      new Set(
        [...index.keys()].filter(
          (id) => (index.get(id).node.children || EMPTY).length,
        ),
      ),
    );
  const zoomBy = (dir) =>
    setZoom(
      (z) =>
        ZOOMS[
          Math.min(
            ZOOMS.length - 1,
            Math.max(
              0,
              ZOOMS.indexOf(
                ZOOMS.reduce((a, b) =>
                  Math.abs(b - z) < Math.abs(a - z) ? b : a,
                ),
              ) + dir,
            ),
          )
        ],
    );
  const fit = () => {
    const canvas = canvasRef.current;
    const content = contentRef.current;
    if (!canvas || !content) return;
    const natural = content.scrollWidth / zoom;
    setZoom(Math.max(0.4, Math.min(1, (canvas.clientWidth - 32) / natural)));
    canvas.scrollTo({ left: 0, top: 0 });
  };

  // Drag to pan (only on the canvas background, not on cards)
  const onPointerDown = (e) => {
    if (e.button !== 0 || e.target.closest(".hc-card")) return;
    const c = canvasRef.current;
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      left: c.scrollLeft,
      top: c.scrollTop,
    };
    c.setPointerCapture?.(e.pointerId);
    c.classList.add("is-panning");
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const c = canvasRef.current;
    c.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
    c.scrollTop = drag.current.top - (e.clientY - drag.current.y);
  };
  const endPan = () => {
    drag.current = null;
    canvasRef.current?.classList.remove("is-panning");
  };

  const showList = narrow || view === "list";

  return (
    <div className="hc">
      <div className="hc-toolbar" role="toolbar" aria-label={`${label} tools`}>
        {searchText && (
          <div className="position-relative hc-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-2 text-slate-500 fs-075"></i>
            <input
              type="search"
              className="form-control form-control-sm ps-4"
              placeholder="Find…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={`Search ${label.toLowerCase()}`}
            />
          </div>
        )}
        {q && !showTable && (
          <span className="fs-075 text-slate-500">{matches.size} found</span>
        )}
        {!showList && !showTable && (
          <>
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={expandAll}
            >
              Expand all
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={() => setOpen(new Set())}
            >
              Collapse all
            </button>
            <div className="btn-group" role="group" aria-label="Zoom">
              <button
                type="button"
                className="btn btn-sm btn-light border"
                onClick={() => zoomBy(-1)}
                aria-label="Zoom out"
              >
                <i className="bi bi-zoom-out"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light border tabular-nums"
                onClick={() => setZoom(1)}
                title="Reset zoom"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light border"
                onClick={() => zoomBy(1)}
                aria-label="Zoom in"
              >
                <i className="bi bi-zoom-in"></i>
              </button>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={fit}
            >
              <i className="bi bi-arrows-angle-contract me-1"></i>Fit
            </button>
          </>
        )}
        {!narrow && !controlledView && (
          <div className="btn-group ms-auto" role="group" aria-label="View">
            <button
              type="button"
              className={`btn btn-sm ${view === "chart" ? "btn-primary" : "btn-light border"}`}
              onClick={() => setView("chart")}
              aria-pressed={view === "chart"}
            >
              <i className="bi bi-diagram-3 me-1"></i>Chart
            </button>
            <button
              type="button"
              className={`btn btn-sm ${view === "list" ? "btn-primary" : "btn-light border"}`}
              onClick={() => setView("list")}
              aria-pressed={view === "list"}
            >
              <i className="bi bi-list-nested me-1"></i>List
            </button>
            {renderTable && (
              <button
                type="button"
                className={`btn btn-sm ${view === "table" ? "btn-primary" : "btn-light border"}`}
                onClick={() => setView("table")}
                aria-pressed={view === "table"}
              >
                <i className="bi bi-table me-1"></i>Table
              </button>
            )}
          </div>
        )}
      </div>

      {showTable ? (
        renderTable(q)
      ) : showList ? (
        <OrgTree
          key={`${nodes.length}-${q}`}
          nodes={nodes}
          openDepth={q ? 99 : openDepth}
          isInactive={isInactive}
          renderNode={(n, d) => (
            <span
              className={`d-contents ${matches.has(n.id) ? "hc-row-match" : ""}`}
            >
              {(renderRow || renderCard)(n, d)}
            </span>
          )}
        />
      ) : (
        <div
          ref={canvasRef}
          className="hc-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPan}
          onPointerCancel={endPan}
        >
          <div ref={contentRef} className="hc-tree" style={{ zoom }}>
            <ul role="tree" aria-label={label}>
              {nodes.map((n) => (
                <ChartNode
                  key={n.id}
                  node={n}
                  depth={0}
                  open={open}
                  toggle={toggle}
                  renderCard={renderCard}
                  isInactive={isInactive}
                  matches={matches}
                  onOpen={onOpen}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchyChart;
